import React, { memo, useCallback } from 'react'
import tw from 'twin.macro'
import { useSelector } from 'react-redux'
import type { GridRowParams } from '@mui/x-data-grid-pro'
import { GridTable } from 'src/components/shared/grid/GridTable'
import type { RetryConfigTemplate } from 'src/interfaces/retryConfigTemplate'
import {
  getRetryConfigTemplates,
  getRetryConfigTemplatesLoading,
} from 'src/selectors/retryConfigTemplate'
import { EmptyRetryTemplates } from './EmptyRetryTemplates'
import { retryTemplateColumns } from './retryTemplateColumns'

interface Props {
  /** Opens the update modal. `null` when the user may not edit templates — rows aren't clickable. */
  onRowClick: ((template: RetryConfigTemplate) => void) | null
}

const getRowId = (row: RetryConfigTemplate): string => {
  return row.id
}

const RetryTemplatesTableComponent = ({ onRowClick }: Props) => {
  const templates = useSelector(getRetryConfigTemplates)
  const isLoading = useSelector(getRetryConfigTemplatesLoading)

  const isEditable = onRowClick !== null

  const handleRowClick = useCallback(
    (params: GridRowParams<RetryConfigTemplate>) => {
      if (onRowClick === null) {
        return
      }

      onRowClick(params.row)
    },
    [onRowClick],
  )

  // No `paginationModel` is passed, so `GridTable` (`pagination={!!paginationModel}`) turns
  // pagination off entirely — every row renders in a scrolling body, not a client-paginated one.
  // `RetryActionType` has one member today, so that scrolling body holds a single row, but the
  // table is built to hold more without a server-mode footer this endpoint has no params for.
  return (
    <GridTable<RetryConfigTemplate>
      rows={templates}
      columns={retryTemplateColumns}
      getRowId={getRowId}
      isLoading={isLoading}
      fillHeight
      EmptyElement={EmptyRetryTemplates}
      className={isEditable ? tw`[& .MuiDataGrid-row]:cursor-pointer` : undefined}
      onRowClick={isEditable ? handleRowClick : undefined}
      tableDataTestId="retry-templates-table"
      rowDataTestIdPrefix="retry-templates"
    />
  )
}

export const RetryTemplatesTable = memo(RetryTemplatesTableComponent)
